import { Flex, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  ButtonText,
  FormFeedback,
  Input,
  OutlineCard,
  TextArea
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { get } from 'lodash';

import {
  FormFieldOpposedReviewers,
  FormFieldRecommendedReviewers
} from '@publishing-engine/domain/types/article';

import { FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import RinggoldSearch, {
  RinggoldOrg
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/RinggoldSearch';
import {
  IDependentValue,
  IReviewerRecommendation
} from '@publishing-engine/domain/models';

export type IReviewersFormField =
  | FormFieldRecommendedReviewers
  | FormFieldOpposedReviewers;

type IReviewersFormFieldName = IReviewersFormField['name'];

export type IReviewerRecommendationValue = {
  preference: {
    recommendedReviewers?: IDependentValue<IReviewerRecommendation[]>;
    opposedReviewers?: IDependentValue<IReviewerRecommendation[]>;
  };
};

export const reviewersTypeLabel: Record<
  IReviewersFormFieldName,
  {
    main: string;
    secondary: string;
  }
> = {
  RecommendedReviewers: {
    main: 'Recommended',
    secondary: 'recommend'
  },
  OpposedReviewers: {
    main: 'Opposed',
    secondary: 'oppose'
  }
} as const;

const DEFAULT_EMPTY_RECORD = {
  firstName: '',
  lastName: '',
  email: '',
  affiliation: '',
  reason: ''
};

const ExpertsRecommended = ({
  config
}: {
  config: FormFieldRecommendedReviewers | FormFieldOpposedReviewers;
}) => {
  const {
    formState: { errors },
    watch,
    control,
    register,
    setValue,
    clearErrors
  } = useFormContext<IReviewerRecommendationValue>();
  const {
    params: { labels },
    caseDataFields: { main: name, dependsOn }
  } = config;
  const dependsOnField = `${name}.${dependsOn}` as const;
  const mainField = `${name}.value` as const;

  const exists = watch(dependsOnField);
  const error = get(errors, name);

  const { fields, append, remove } = useFieldArray({
    control,
    name: mainField
  });

  const addRow = () => {
    append(DEFAULT_EMPTY_RECORD);
  };

  if (exists && !fields.length) {
    addRow();
  }

  if (!exists) {
    setValue(mainField, undefined);
  }

  const handleOrganizationSelection = (
    org: Partial<RinggoldOrg>,
    index: number
  ) => {
    // update the selected org values in form
    org.name &&
      setValue(`${mainField}.${index}.affiliation`, org.name, {
        shouldValidate: true,
        shouldDirty: true
      });
    org.ringgoldId &&
      setValue(`${mainField}.${index}.ringgoldId`, org.ringgoldId, {
        shouldValidate: true,
        shouldDirty: true
      });
    org.country &&
      setValue(`${mainField}.${index}.country`, org.country, {
        shouldValidate: true,
        shouldDirty: true
      });
    org.countryCode &&
      setValue(`${mainField}.${index}.countryCode`, org.countryCode, {
        shouldValidate: true,
        shouldDirty: true
      });
  };

  const handleRinggoldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.trim();
    // if input value is cleared in that case reset the ringgoldId as well
    if (!value.length) {
      setValue(`${mainField}.${index}.affiliation`, '', {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue(`${mainField}.${index}.ringgoldId`, undefined, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };

  const reviewerLabel = reviewersTypeLabel[config.name];
  return (
    <OutlineCard borderless width="100%" padding={0}>
      {/* ----- Data Availability ----- */}
      <Flex flexDirection="column" gap={8} width="100%">
        <FieldLabel label={labels.main} subText={labels.subtitle} isRequired />

        <Flex flexDirection="column" gap={4}>
          <RadioGroup
            control={control}
            name={dependsOnField}
            options={[
              {
                value: false,
                children: labels.options['false']?.main
              },
              {
                value: true,
                children: labels.options['true']?.main
              }
            ]}
          />

          {error?.exists && (
            <FormFeedback variant="error">
              Please indicate whether you would like to{' '}
              {reviewerLabel.secondary} specific Reviewers to proceed.
            </FormFeedback>
          )}
        </Flex>

        {/* ----- Reviewer Form ----- */}
        {exists ? (
          <>
            <Text textStyle="headingItem">{reviewerLabel.main} Reviewers</Text>
            <OutlineCard borderless width="100%">
              <Flex
                flexDirection="column"
                gap={8}
                alignItems="flex-start"
                width={'100%'}
              >
                {fields.map((field, index) => {
                  const country = watch(`${mainField}.${index}.country`);
                  const countryCode =
                    watch(`${mainField}.${index}.countryCode`) ?? '';
                  const ringgoldId = watch(`${mainField}.${index}.ringgoldId`);

                  return (
                    <OutlineCard
                      key={field.id}
                      borderless
                      width="100%"
                      padding={8}
                      bgColor={'surfaceSecondaryFill'}
                    >
                      <Flex flexDirection="column" gap={4} width="100%">
                        <Flex width="100%" gap={4}>
                          {/* --- First Name --- */}
                          <Flex flexDirection="column" flex="1" gap={2}>
                            <FieldLabel
                              label="Given name"
                              subText=""
                              isRequired
                            />

                            <Input
                              {...register(`${mainField}.${index}.firstName`)}
                              placeholder="Enter the Reviewer’s given name"
                              isInvalid={Boolean(
                                error?.value?.[index]?.firstName
                              )}
                            />
                            {error?.value?.[index]?.firstName ? (
                              <FormFeedback variant="error">
                                Given name is required
                              </FormFeedback>
                            ) : null}
                          </Flex>

                          {/* --- Last Name --- */}
                          <Flex flexDirection="column" flex="1" gap={2}>
                            <FieldLabel
                              label="Family name"
                              subText=""
                              isRequired
                            />

                            <Input
                              {...register(`${mainField}.${index}.lastName`)}
                              placeholder="Enter the Reviewer’s family name"
                              isInvalid={Boolean(
                                error?.value?.[index]?.lastName
                              )}
                            />
                            {error?.value?.[index]?.lastName ? (
                              <FormFeedback variant="error">
                                Family name is required
                              </FormFeedback>
                            ) : null}
                          </Flex>
                        </Flex>

                        {/* --- Email --- */}
                        <Flex flexDirection="column" flex="1" gap={2}>
                          <FieldLabel
                            label="Professional Email"
                            subText="Provide the Reviewer’s professional email."
                            isRequired
                          />

                          <Input
                            type="email"
                            {...register(`${mainField}.${index}.email`)}
                            placeholder="e.g., fabius@molecularconnections.com"
                            isInvalid={Boolean(error?.value?.[index]?.email)}
                          />
                          {error?.value?.[index]?.email ? (
                            <FormFeedback variant="error">
                              Invalid email
                            </FormFeedback>
                          ) : null}
                        </Flex>

                        {/* --- Affiliation --- */}
                        <Flex flexDirection="column" flex="1" gap={1}>
                          <FieldLabel
                            label="Affiliated Organisation"
                            subText="Name the Reviewer’s current affiliated organisation. Affiliation information is sourced from the Ringgold database."
                            isRequired
                          />

                          <RinggoldSearch
                            {...register(`${mainField}.${index}.affiliation`)}
                            placeholder="Enter an Affiliation"
                            isInvalid={Boolean(
                              error?.value?.[index]?.affiliation
                            )}
                            onUpdate={data => {
                              handleOrganizationSelection(data, index);
                              clearErrors(`${mainField}.${index}.affiliation`);
                            }}
                            onChange={e => handleRinggoldChange(e, index)}
                            selectedCountry={
                              country ? { country, countryCode } : null
                            }
                            selectedRinggoldId={ringgoldId ?? ''}
                            error={{
                              affiliation: error?.value?.[index]?.affiliation,
                              country: error?.value?.[index]?.country
                            }}
                          />
                        </Flex>

                        {/* --- Reason --- */}
                        <Flex flexDirection="column" flex="1" gap={1}>
                          <FieldLabel
                            label="Reason"
                            subText="Provide the reason behind this Reviewer’s recommendation."
                            isRequired
                          />

                          <TextArea
                            value={watch(`${mainField}.${index}.reason`)}
                            {...register(`${mainField}.${index}.reason`)}
                            isInvalid={Boolean(error?.value?.[index]?.reason)}
                            placeholder="Enter a reason"
                          />

                          {error?.value?.[index]?.reason ? (
                            <FormFeedback variant="error">
                              Please enter your reason
                            </FormFeedback>
                          ) : null}
                        </Flex>

                        <ButtonText
                          alignSelf={'flex-end'}
                          iconLeft={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => remove(index)}
                        >
                          Delete Reviewer
                        </ButtonText>
                      </Flex>
                    </OutlineCard>
                  );
                })}

                <Flex
                  alignItems="center"
                  justifyContent="space-between" // Keep space-between to allow for alignment
                  width="100%"
                >
                  {error?.value && !Array.isArray(error.value) && (
                    <FormFeedback variant="error">
                      At least one reviewer is required
                    </FormFeedback>
                  )}

                  <ButtonText
                    alignSelf={'flex-start'}
                    iconLeft={<FontAwesomeIcon icon={faPlus} />}
                    onClick={addRow}
                    marginLeft="auto" // Added marginLeft to push the button to the right
                  >
                    Add {fields.length > 0 ? 'another' : ''} Reviewer
                  </ButtonText>
                </Flex>
              </Flex>
            </OutlineCard>
          </>
        ) : null}
      </Flex>
    </OutlineCard>
  );
};

export default ExpertsRecommended;
